import { useCallback, useMemo } from "react";
import { create } from "zustand";

/**
 * 搜索反馈类型枚举
 */
export enum SearchFeedbackTypeEnum {
  POSITIVE = "POSITIVE", // 用户点击"喜欢"
  NEGATIVE = "NEGATIVE", // 用户点击"不喜欢"
}

/**
 * 反馈项接口
 */
export interface FeedbackItem {
  fileId: number;
  feedbackType: SearchFeedbackTypeEnum;
  /** 用于展示的缩略图 URL */
  thumbnailUrl?: string;
}

/**
 * 反馈搜索状态存储
 */
interface FeedbackSearchState {
  /** 当前搜索词 */
  searchWord: string;
  /** 反馈列表 */
  feedbacks: FeedbackItem[];
  /** 设置搜索词（切换搜索词时清空反馈） */
  setSearchWord: (word: string) => void;
  /** 添加反馈 */
  addFeedback: (item: FeedbackItem) => void;
  /** 移除反馈 */
  removeFeedback: (fileId: number) => void;
  /** 清空所有反馈 */
  clearFeedbacks: () => void;
  /** 触发搜索的版本号（每次变化时重新搜索） */
  searchVersion: number;
  /** 触发搜索 */
  triggerSearch: () => void;
}

/**
 * 反馈搜索状态 store
 */
export const useFeedbackSearchStore = create<FeedbackSearchState>(
  (set, get) => ({
    searchWord: "",
    feedbacks: [],
    searchVersion: 0,

    setSearchWord: (word: string) => {
      const currentWord = get().searchWord;
      if (currentWord !== word) {
        // 切换搜索词时清空反馈
        set({ searchWord: word, feedbacks: [], searchVersion: 0 });
      }
    },

    addFeedback: (item: FeedbackItem) => {
      set((state) => {
        // 如果已存在相同 fileId 的反馈，先移除
        const filtered = state.feedbacks.filter(
          (f) => f.fileId !== item.fileId
        );
        return {
          feedbacks: [...filtered, item],
        };
      });
    },

    removeFeedback: (fileId: number) => {
      set((state) => ({
        feedbacks: state.feedbacks.filter((f) => f.fileId !== fileId),
      }));
    },

    clearFeedbacks: () => {
      set({ feedbacks: [] });
    },

    triggerSearch: () => {
      set((state) => ({
        searchVersion: state.searchVersion + 1,
      }));
    },
  })
);

/**
 * 反馈搜索 Hook
 * @param searchWord 当前搜索词
 */
export function useFeedbackSearch(searchWord?: string) {
  const {
    feedbacks,
    addFeedback,
    removeFeedback,
    clearFeedbacks,
    setSearchWord,
    searchVersion,
    triggerSearch,
  } = useFeedbackSearchStore();

  // 同步搜索词
  if (searchWord !== undefined) {
    const currentWord = useFeedbackSearchStore.getState().searchWord;
    if (currentWord !== searchWord) {
      setSearchWord(searchWord);
    }
  }

  // 喜欢的列表
  const positiveFeedbacks = useMemo(
    () =>
      feedbacks.filter(
        (f) => f.feedbackType === SearchFeedbackTypeEnum.POSITIVE
      ),
    [feedbacks]
  );

  // 不喜欢的列表
  const negativeFeedbacks = useMemo(
    () =>
      feedbacks.filter(
        (f) => f.feedbackType === SearchFeedbackTypeEnum.NEGATIVE
      ),
    [feedbacks]
  );

  // 添加喜欢
  const addPositive = useCallback(
    (fileId: number, thumbnailUrl?: string) => {
      addFeedback({
        fileId,
        feedbackType: SearchFeedbackTypeEnum.POSITIVE,
        thumbnailUrl,
      });
      triggerSearch();
    },
    [addFeedback, triggerSearch]
  );

  // 添加不喜欢
  const addNegative = useCallback(
    (fileId: number, thumbnailUrl?: string) => {
      addFeedback({
        fileId,
        feedbackType: SearchFeedbackTypeEnum.NEGATIVE,
        thumbnailUrl,
      });
      triggerSearch();
    },
    [addFeedback, triggerSearch]
  );

  // 移除反馈并触发搜索
  const removeFeedbackAndSearch = useCallback(
    (fileId: number) => {
      removeFeedback(fileId);
      triggerSearch();
    },
    [removeFeedback, triggerSearch]
  );

  // 获取用于 API 请求的 feedbacks 参数
  const getFeedbacksForApi = useCallback(() => {
    if (feedbacks.length === 0) return undefined;
    return feedbacks.map((f) => ({
      fileId: f.fileId,
      feedbackType: f.feedbackType,
    }));
  }, [feedbacks]);

  // 检查某个 fileId 是否已有反馈
  const getFeedbackType = useCallback(
    (fileId: number) => {
      const feedback = feedbacks.find((f) => f.fileId === fileId);
      return feedback?.feedbackType;
    },
    [feedbacks]
  );

  return {
    feedbacks,
    positiveFeedbacks,
    negativeFeedbacks,
    addPositive,
    addNegative,
    removeFeedback: removeFeedbackAndSearch,
    clearFeedbacks,
    getFeedbacksForApi,
    getFeedbackType,
    searchVersion,
    hasFeedbacks: feedbacks.length > 0,
  };
}
